// LiveStream.jsx
'use strict';
import React from 'react';
import PropTypes from 'prop-types';
import styled, { css, keyframes } from 'styled-components';
import { withTranslation } from 'react-i18next';
import SwipeableViews from 'react-swipeable-views';

import LivestreamDetailOnlineHeader from '../container/LivestreamDetailOnlineHeader.js';
import LivestreamDetailOnlineFooterMobile from '../container/LivestreamDetailOnlineFooterMobile.js';
import LivestreamDetailModals from '../container/LivestreamDetailModals.js';
import LivestreamDetailOnlineFooterDesktop from '../container/LivestreamDetailOnlineFooterDesktop.js';
import DecryptionWrapper from '../container/DecryptionWrapper.js';
import LivestreamWatermark from '../container/LivestreamWatermark.js';
import LivestreamUserTriggerModals from '../container/LivestreamUserTriggerModals.js';
import LiveStreamAnimationGift from '../container/LiveStreamAnimationGift.js';
import LivestreamScrollHint from '../container/LivestreamScrollHint.js';
import LivestreamOverlayCta from '../container/LivestreamOverlayCta.js';

import LiveStreamFloatingAnimation from '../container/LiveStreamFloatingAnimation.js';
import ModalView from '../container/ModalView.js';

import LivestreamDetailOffline from '../component/LivestreamDetailOffline.jsx';
import UniversalComponent from '../component/UniversalComponent.jsx';
import Loading, { LoadingType } from '../component/Loading.jsx';
import StatefulImage from '../component/StatefulImage.jsx';
import ModalTracking from '../component/ModalTracking.jsx';
import CleanModeHint from '../component/CleanModeHint.jsx';
import HorizonLayoutHint from '../component/HorizonLayoutHint.jsx';
import AsideModuleHint from '../component/AsideModuleHint.jsx';

import { ButtonId, ModalId } from '../resource/mixpanel.js';
import {
  PRESET_PREVIEW,
  ASIDE_WIDTH,
  privateShowStatus as privateShowStatusConstant,
  LivestreamUIMode,
} from '../resource/liveStreamConstants.js';
import { drmRulesetTestStatus as drmRulesetTestStatusConstant } from '../resource/drmConstants.js';
import { COM_APPLE_FPS } from '../resource/drmConstants.js';
import { getUserPicture, MediaAssetFormat } from '../resource/getMediaAsset.js';
import { TranslationNamespace } from '../resource/translationNamespace.js';
import { PRESET_SD } from '../resource/liveStreamConstants.js';

import media, { mediaHeight } from '../style/media.js';
import {
  getIsByteplusStreamProvider,
  getIsTencentStreamProvider,
  getIsAgoraStreamProvider,
} from '../resource/livestreamUtils.js';

import useLivestreamRatingHint from '../hook/useLivestreamRatingHint.js';

import ResetButtonStyle from '../style/ResetButtonStyle.js';
import {
  headerHeight,
  textColor,
  iPhoneXSeriesFooterPaddingInPWA,
  color,
} from '../style/variables.js';
import { getIsIphoneXSeriesPWA } from '../resource/getUserAgent.js';
import { NULL_FUNCTION } from '../resource/defaults.js';

import CoverPlaceHolder from '../../img/placeholder-bg-logowall.svg';
import BackIconSrc from '../../img/ic-topbar-close.svg';
import PlayPaddingIconSource from '../../img/ic_play_padding.svg';
const modalViewRootId = 'stream-container';
const isIphoneXSeriesPWA = getIsIphoneXSeriesPWA();

export const PlayerContext = React.createContext(null);

const CLEAN_MODE_HINT_AUTO_CLOSE_DELAY = 6000;
const HORIZON_LAYOUT_TIMEOUT = 3000;
const ASIDE_MODULE_HINT_AUTO_CLOSE_DELAY = 6000;

export class LiveStream extends React.PureComponent {
  state = {
    shouldReloadPlayer: false,
    activeUIIndex: LivestreamUIMode.NORMAL,
    player: null,
  };
  onlineContainerRef = React.createRef();
  playerContainerRef = React.createRef();
  nextTick = null;
  cleanModeHintTimeout = null;
  horizonLayoutTimeout = null;
  asideModuleHintTimeout = null;

  // Prefetch private enc stream retained events for isNearBy livestreams
  prefetchPrivateEncStreamRetainedEvents = () => {
    const { isNearBy, streamId, provider, fetchRetainedEvents } = this.props;
    if (isNearBy && streamId && !provider) {
      fetchRetainedEvents({ channels: [`private-enc-stream@${streamId}`] });
    }
  };

  componentDidMount() {
    // Prefetch private enc stream retained events for isNearBy livestreams
    this.prefetchPrivateEncStreamRetainedEvents();
  }

  componentDidUpdate(prevProps) {
    const {
      isActive,
      isLiveStreaming,
      resetLiveStream,
      streamId,
      isExclusivePrivatePerforming,
      hasStreamToken,
      isMeExclusivePrivateBuyer,
      isLivestreamModerator,
      fetchStreamsToken,
      updateLiveStreamPreset,
      isNearBy,
      provider,
    } = this.props;
    if (
      isActive &&
      isExclusivePrivatePerforming &&
      isExclusivePrivatePerforming !== prevProps.isExclusivePrivatePerforming
    ) {
      // needs to refresh stream token to prevent exclusive private buyer use livestream pass to watch exclusive private stream without pay.
      if (isMeExclusivePrivateBuyer && hasStreamToken) {
        fetchStreamsToken({ streamId });
      }
      // clear stream token to prevent non-exclusive private buyer use livestream pass to watch exclusive private
      if (!isLivestreamModerator) {
        updateLiveStreamPreset({
          streamId,
          preset: PRESET_PREVIEW,
          pusherPreset: PRESET_PREVIEW,
        });
      }
    }
    if (isActive !== prevProps.isActive) {
      if (!isActive) {
        this.closeLiveStreamInformation();
        resetLiveStream({ streamId });
      } else {
        this.setState({
          activeUIIndex: LivestreamUIMode.NORMAL,
        });
      }
    }
    if (
      isActive !== prevProps.isActive ||
      isLiveStreaming !== prevProps.isLiveStreaming
    ) {
      if (isActive && isLiveStreaming === false) {
        this.closeKaraokeModal();
      }
    }

    // Check if isNearBy, streamId, or provider changed to prefetch private enc stream retained events
    if (
      isNearBy !== prevProps.isNearBy ||
      streamId !== prevProps.streamId ||
      provider !== prevProps.provider
    ) {
      this.prefetchPrivateEncStreamRetainedEvents();
    }

    // Display logic priority: Compare video width and height first.
    // If width > height, prioritize showing horizon layout hint.
    // Only check clean mode hint after horizon layout hint is not displayed.
    if (this.getIsDisplayHorizonLayoutHint() && !this.horizonLayoutTimeout) {
      this.horizonLayoutTimeout = setTimeout(
        this.handleCloseHorizonLayoutHint,
        HORIZON_LAYOUT_TIMEOUT
      );
    }

    if (this.getIsDisplayCleanModeHint() && !this.cleanModeHintTimeout) {
      this.cleanModeHintTimeout = setTimeout(
        this.handleCloseCleanModeHint,
        CLEAN_MODE_HINT_AUTO_CLOSE_DELAY
      );
    }

    if (this.getIsDisplayAsideModuleHint() && !this.asideModuleHintTimeout) {
      this.asideModuleHintTimeout = setTimeout(
        this.handleCloseAsideModuleHint,
        ASIDE_MODULE_HINT_AUTO_CLOSE_DELAY
      );
    }
  }
  getIsHorizonLayoutMovie = () => {
    const { videoWidth, videoHeight } = this.props;
    if (videoWidth === null || videoHeight === null) {
      return false;
    }
    return videoWidth > videoHeight;
  };
  componentWillUnmount() {
    const { streamId, resetLiveStream, isActive } = this.props;
    if (isActive) {
      this.closeLiveStreamInformation();
      resetLiveStream({ streamId });
    }

    if (this.getIsDisplayHorizonLayoutHint()) {
      this.handleCloseHorizonLayoutHint();
    }

    // If a user enters the livestream and leaves within 6 seconds after the player has started playing,
    // the clean mode hint will still be dismissed.
    // As a result, the hint will not be shown the next time the user enters the livestream.
    if (this.getIsDisplayCleanModeHint()) {
      this.handleCloseCleanModeHint();
    }

    if (this.getIsDisplayAsideModuleHint()) {
      this.handleCloseAsideModuleHint();
    }

    this.nextTick && clearTimeout(this.nextTick);
  }
  getIsLoading = () => {
    const { isLiveStreaming, isPrivateStreamSubscribed } = this.props;
    return isLiveStreaming === null && !isPrivateStreamSubscribed;
  };
  handleClickBack = () => {
    const {
      openModal,
      shouldShowExitAlertModal,
      isPrivateShowUnlocked,
      isPrivateStreamUnlocked,
      goBack,
    } = this.props;
    if (
      shouldShowExitAlertModal &&
      (isPrivateShowUnlocked || isPrivateStreamUnlocked)
    ) {
      return openModal({
        id: 'LeaveLivestreamToListConfirmModal',
      });
    }
    return goBack();
  };
  closeKaraokeModal = () => {
    const { closeModal } = this.props;
    closeModal({ ids: ['LiveStreamKaraokeModal'] });
  };
  renderLiveStreamCover = ({ showBackgroundGradient = false } = {}) => {
    const { userId, username, meId } = this.props;
    return (
      <LiveStreamCover>
        <StatefulImage>
          <LoadingWrapper data-key="loading">
            <Loading type={LoadingType.DARK} size={40} />
          </LoadingWrapper>
          <Image data-key="error" src={CoverPlaceHolder} />
          <Picture data-key="target">
            <DecryptionWrapper
              resourceUrl={
                getUserPicture({
                  userId,
                  meId,
                  size: 512,
                  format: MediaAssetFormat.JPG,
                }).href
              }
            >
              <source
                type="image/webp"
                srcSet={`${
                  getUserPicture({
                    userId,
                    meId,
                    size: 512,
                    format: MediaAssetFormat.WEBP,
                  }).href
                } 1x, ${
                  getUserPicture({
                    userId,
                    meId,
                    size: 1024,
                    format: MediaAssetFormat.WEBP,
                  }).href
                } 2x`}
              />
              <img
                alt={`${username}'s cover`}
                srcSet={`${
                  getUserPicture({
                    userId,
                    meId,
                    size: 512,
                    format: MediaAssetFormat.JPG,
                  }).href
                } 1x, ${
                  getUserPicture({
                    userId,
                    meId,
                    size: 1024,
                    format: MediaAssetFormat.JPG,
                  }).href
                } 2x`}
              />
            </DecryptionWrapper>
          </Picture>
        </StatefulImage>
        {this.getIsLoading() && (
          <LoadingWrapper>
            <Loading type={LoadingType.DARK} size={40} />
          </LoadingWrapper>
        )}
        <LiveStreamCoverMask $showBackgroundGradient={showBackgroundGradient} />
      </LiveStreamCover>
    );
  };
  renderLiveStreamBlurredCover = () => (
    <BlurredCover>
      {this.renderLiveStreamCover({ showBackgroundGradient: true })}
    </BlurredCover>
  );
  renderClosePanel = () => {
    return (
      <ClosePanel>
        <BackButton
          data-element_id={ButtonId.All.ButtonClose}
          onClick={this.handleClickBack}
        >
          <img src={BackIconSrc} alt="Back" />
        </BackButton>
      </ClosePanel>
    );
  };
  renderStreamerPinned = () => {
    const { streamId } = this.props;
    return (
      <UniversalComponent
        filename="Container_LivestreamPinnedMessages"
        streamId={streamId}
        importer={
          /* #__LOADABLE__ */ () =>
            import('../container/LivestreamPinnedMessages.js')
        }
      />
    );
  };
  closeLiveStreamInformation = () => {
    const { closeModal } = this.props;
    closeModal({ ids: ['LiveStreamInformationModal'] });
  };
  reloadPlayer = () => {
    this.setState({
      shouldReloadPlayer: true,
    });
    this.nextTick = setTimeout(() => {
      this.setState({
        shouldReloadPlayer: false,
      });
    }, 0);
  };
  getIsOverlayCta = () => {
    const { streamStatus, isLivestreamOverlayCtaEnabled, hasStreamToken } =
      this.props;
    return (
      isLivestreamOverlayCtaEnabled &&
      PRESET_SD === streamStatus &&
      !hasStreamToken
    );
  };
  getIsPerforming = () => {
    const { privateShowStatus } = this.props;
    return [
      privateShowStatusConstant.PERFORMING,
      privateShowStatusConstant.PREPARING,
    ].includes(privateShowStatus);
  };

  renderCta = () => {
    const { streamId } = this.props;
    return (
      <ModalTracking id="LiveStreamCountDown">
        <UniversalComponent
          filename="Container_LiveStreamCountDown"
          streamId={streamId}
          importer={
            /* #__LOADABLE__ */ () =>
              import('../container/LiveStreamCountDown.js')
          }
        />
      </ModalTracking>
    );
  };

  renderRetryPlayButton = () => {
    const {
      shouldShowRetryPlayButton,
      setShouldShowRetryPlayButton,
      streamId,
      setPlayerData,
      cleanupAgoraToken,
    } = this.props;
    if (!shouldShowRetryPlayButton) return null;

    return (
      <RetryPlayButton
        data-element_id={ButtonId.LiveStream.ButtonRetryPlay}
        data-tracking_payload={{
          streamId,
        }}
        onClick={async () => {
          const isTencentPlayer = this.shouldUseTencentPlayer();
          const isAgoraPlayer = this.shouldUseAgoraPlayer();

          if (isTencentPlayer) {
            const { retryPlayWithMuted } = await import(
              '../resource/tencentPlayer.js'
            );

            retryPlayWithMuted({ isMuted: true });
            setShouldShowRetryPlayButton({ shouldShow: false });
            return;
          }

          if (isAgoraPlayer) {
            const { getSingletonVideoElement } = await import(
              '../resource/getSingletonVideoElement.js'
            );

            const videoElement = getSingletonVideoElement();
            videoElement.muted = true;
            setPlayerData({ isMuted: true });
            cleanupAgoraToken({ streamId });
          }

          this.reloadPlayer();
          setShouldShowRetryPlayButton({ shouldShow: false });
        }}
      >
        <img src={PlayPaddingIconSource} width="60" height="60" alt="" />
      </RetryPlayButton>
    );
  };
  shouldUseTencentPlayer = () => {
    const { provider } = this.props;
    let result = false;
    try {
      result = getIsTencentStreamProvider({ provider });
    } catch (_) {
      //
    }
    return result;
  };
  shouldUseByteplusPlayer = () => {
    const { provider } = this.props;
    let result = false;
    try {
      result =
        getIsByteplusStreamProvider({ provider }) ||
        !!new URLSearchParams(window.location.search).get('byteplus');
    } catch (_) {
      //
    }
    return result;
  };
  shouldUseAgoraPlayer = () => {
    const { provider } = this.props;
    let result = false;
    try {
      result = getIsAgoraStreamProvider({ provider });
    } catch (_) {
      //
    }
    return result;
  };
  shouldUseDashJsPlayer = () => {
    const { keySystem, preset, publicSourceFormat, privateSourceFormat } =
      this.props;
    let urlFlv = '';
    try {
      urlFlv = new URLSearchParams(window.location.search).get('flv');
    } catch (_) {
      //
    }
    const shouldUseFlv =
      urlFlv ||
      (preset === PRESET_SD
        ? privateSourceFormat === 'flv'
        : publicSourceFormat === 'flv');
    return !shouldUseFlv && COM_APPLE_FPS !== keySystem;
  };

  renderPreloader = () => {
    const { streamId } = this.props;
    if (this.shouldUseAgoraPlayer()) {
      return (
        <UniversalComponent
          filename="Container_LivestreamPreloaderAgora"
          streamId={streamId}
          importer={
            /* #__LOADABLE__ */ () =>
              import('../container/LivestreamPreloaderAgora.js')
          }
        />
      );
    }

    return null;
  };

  renderPlayer = () => {
    const { streamId, sourceId, t } = this.props;
    const { shouldReloadPlayer, activeUIIndex } = this.state;
    if (shouldReloadPlayer) return null;
    return sourceId ? (
      <PlayerWrapper ref={this.playerContainerRef}>
        {this.shouldUseByteplusPlayer() ? (
          <>
            <LivestreamWatermark
              streamId={streamId}
              containerRef={this.playerContainerRef}
            />
            <UniversalComponent
              filename="Container_LivestreamPlayerByteplus"
              streamId={streamId}
              importer={
                /* #__LOADABLE__ */ () =>
                  import('../container/LivestreamPlayerByteplus.js')
              }
            />
          </>
        ) : this.shouldUseTencentPlayer() ? (
          <>
            <LivestreamWatermark
              streamId={streamId}
              containerRef={this.playerContainerRef}
            />
            <UniversalComponent
              filename="Container_LivestreamPlayerTencent"
              streamId={streamId}
              importer={
                /* #__LOADABLE__ */ () =>
                  import('../container/LivestreamPlayerTencent.js')
              }
            />
          </>
        ) : this.shouldUseAgoraPlayer() ? (
          <>
            <LivestreamWatermark
              streamId={streamId}
              containerRef={this.playerContainerRef}
            />
            <UniversalComponent
              filename="Container_LivestreamPlayerAgora"
              streamId={streamId}
              importer={
                /* #__LOADABLE__ */ () =>
                  import('../container/LivestreamPlayerAgora.js')
              }
            />
          </>
        ) : this.shouldUseDashJsPlayer() ? (
          <UniversalComponent
            filename="Container_LiveStreamPlayerDashJs"
            streamId={streamId}
            importer={
              /* #__LOADABLE__ */ () =>
                import('../container/LiveStreamPlayerDashJs.js')
            }
          />
        ) : (
          <UniversalComponent
            filename="Container_LiveStreamPlayer"
            streamId={streamId}
            importer={
              /* #__LOADABLE__ */ () =>
                import('../container/LiveStreamPlayer.js')
            }
          />
        )}
        <LiveStreamPlayerMask
          maskOpacity={activeUIIndex === LivestreamUIMode.NORMAL ? 0.6 : 0}
        />
      </PlayerWrapper>
    ) : (
      <LoadingWithTextWrapper>
        <Loading type={LoadingType.GRAY} size={40} />
        <LoadingText>
          {t('stream_source_disconnected', {
            ns: TranslationNamespace.LIVESTREAM,
          })}
        </LoadingText>
      </LoadingWithTextWrapper>
    );
  };

  renderScrollHintUI = () => {
    const { hasScrollHintDisplayed, isOnMobile, shouldShowOperationHintUI } =
      this.props;
    if (hasScrollHintDisplayed || !shouldShowOperationHintUI || !isOnMobile)
      return null;

    return (
      <ScrollHintUIWrapper>
        <LivestreamScrollHint />
      </ScrollHintUIWrapper>
    );
  };

  renderOfflinePanels = ({ isExclusivePrivateLayout = false } = {}) => {
    const { streamId, isActive, isNearBy } = this.props;
    return (
      <>
        {(isActive || isNearBy) && this.renderLiveStreamCover()}
        {isActive && (
          <LivestreamDetailOffline
            streamId={streamId}
            isExclusivePrivateLayout={isExclusivePrivateLayout}
          />
        )}
        {isActive && this.renderClosePanel()}
      </>
    );
  };
  handleSlideChange = index => {
    this.setState({
      activeUIIndex: index,
    });
  };
  /**
   * Check if clean mode swipe hint should be displayed.
   */
  getIsDisplayCleanModeHint = () => {
    const { shouldDisplayCleanModeHint, isOnMobile } = this.props;
    const { activeUIIndex } = this.state;
    if (this.getIsHorizonLayoutMovie() && this.getIsDisplayHorizonLayoutHint())
      return false;

    const isCleanMode = activeUIIndex === LivestreamUIMode.CLEAN;

    return (
      // Only show clean mode swipe hint when user is in clean mode
      isCleanMode &&
      shouldDisplayCleanModeHint &&
      isOnMobile &&
      this.getShouldRenderPlayer() &&
      // Prevent showing clean mode hint immediately after horizon layout hint is displayed
      !this.getIsDisplayHorizonLayoutHint()
    );
  };
  getIsDisplayHorizonLayoutHint = () => {
    const { shouldDisplayHorizonLayoutHint, isOnMobile } = this.props;
    return (
      shouldDisplayHorizonLayoutHint &&
      isOnMobile &&
      this.getShouldRenderPlayer() &&
      this.getIsHorizonLayoutMovie()
    );
  };
  getIsDisplayAsideModuleHint = () => {
    const { shouldDisplayAsideModuleHint, isOnMobile, isAsideExpanded } =
      this.props;
    const { activeUIIndex } = this.state;
    const isCleanMode = activeUIIndex === LivestreamUIMode.CLEAN;

    return (
      shouldDisplayAsideModuleHint &&
      !isOnMobile &&
      !isCleanMode &&
      isAsideExpanded &&
      this.getShouldRenderPlayer()
    );
  };
  handleCloseCleanModeHint = () => {
    const { setShouldDisplayCleanModeHint } = this.props;

    // First-time viewing on this device && mobile device && player finished rendering → show CleanModeHint
    // Close the CleanModeHint component on click && persist the state to IndexedDB
    setShouldDisplayCleanModeHint({
      shouldDisplayCleanModeHint: false,
    });

    this.cleanModeHintTimeout && clearTimeout(this.cleanModeHintTimeout);
  };
  handleCloseAsideModuleHint = () => {
    const { setShouldDisplayAsideModuleHint } = this.props;

    setShouldDisplayAsideModuleHint({
      shouldDisplayAsideModuleHint: false,
    });

    this.asideModuleHintTimeout && clearTimeout(this.asideModuleHintTimeout);
  };
  handleCloseHorizonLayoutHint = () => {
    const { setShouldDisplayHorizonLayoutHint } = this.props;

    setShouldDisplayHorizonLayoutHint({
      shouldDisplayHorizonLayoutHint: false,
    });

    this.horizonLayoutTimeout && clearTimeout(this.clearHorizonLayoutTimeout);
  };
  getIsDrmTesting = () => {
    const { drmTestStatus } = this.props;
    return drmRulesetTestStatusConstant.TESTING === drmTestStatus;
  };
  getNeedStreamTokenForPresetSD = () => {
    const { streamStatus, hasStreamToken } = this.props;
    return streamStatus === PRESET_SD && !hasStreamToken;
  };
  getShouldRenderPlayer = () => {
    const { isActive } = this.props;
    return (
      isActive &&
      !this.getIsLoading() &&
      !this.getIsDrmTesting() &&
      !this.getNeedStreamTokenForPresetSD()
    );
  };
  renderOnlinePanels = () => {
    const {
      isGiftPanelOpen,
      isChatPanelOpen,
      isAsideExpanded,
      isTopLeaderboardScrolling,
      isActive,
      isNearBy,
      isOnMobile,
      isMeExclusivePrivateBuyer,
      needStreamTokenForExclusivePrivatePerforming,
      shouldShowLivestreamCta,
      deviceHeight,
      userId,
      streamId,
      fundingId,
      streamStatus,
      hasStreamToken,
      isExclusivePrivatePerforming,
      isExclusivePrivatePreparing,
    } = this.props;
    const { activeUIIndex, player } = this.state;
    const isPerforming = this.getIsPerforming();
    const isOverlayCta = this.getIsOverlayCta();
    const isDrmTesting = this.getIsDrmTesting();
    const isLoading = this.getIsLoading();
    const isCleanMode = activeUIIndex === LivestreamUIMode.CLEAN;
    const needStreamTokenForPresetSD = this.getNeedStreamTokenForPresetSD();
    const shouldDisableSwiper =
      isGiftPanelOpen || isChatPanelOpen || isTopLeaderboardScrolling;
    const shouldDisplayCover =
      (isActive && (isLoading || isDrmTesting)) ||
      (!isActive && isNearBy) ||
      needStreamTokenForPresetSD;
    const isExclusivePrivateOngoing =
      isExclusivePrivatePerforming || isExclusivePrivatePreparing;
    const shouldDisplayExclusivePrivateOverlayCta =
      isMeExclusivePrivateBuyer && needStreamTokenForExclusivePrivatePerforming;

    // Fallback to window.innerHeight when Redux deviceHeight is not available yet
    const fallbackDeviceHeight =
      deviceHeight || (typeof window !== 'undefined' ? window.innerHeight : 0);

    const SwipableUIHeight = isOnMobile
      ? fallbackDeviceHeight
      : fallbackDeviceHeight - headerHeight;

    return (
      <Container
        onClick={() => {
          if (this.getIsDisplayHorizonLayoutHint()) {
            this.handleCloseHorizonLayoutHint();
            return;
          }
          if (this.getIsDisplayCleanModeHint()) {
            this.handleCloseCleanModeHint();
            return;
          }
          if (this.getIsDisplayAsideModuleHint()) {
            this.handleCloseAsideModuleHint();
            return;
          }
        }}
      >
        <PlayerContext.Provider
          value={{
            player,
            setContextPlayer: player => {
              this.setState({
                player,
              });
            },
          }}
        >
          <StreamSide id={isActive ? modalViewRootId : undefined}>
            {this.getIsDisplayCleanModeHint() && <CleanModeHint />}
            {this.getIsDisplayHorizonLayoutHint() && <HorizonLayoutHint />}
            {this.getIsDisplayAsideModuleHint() && <AsideModuleHint />}
            {shouldDisplayCover &&
              (needStreamTokenForPresetSD
                ? this.renderLiveStreamBlurredCover()
                : this.renderLiveStreamCover())}
            {isDrmTesting && (
              <LoadingWrapper>
                <Loading type={LoadingType.YELLOW} size={40} />
              </LoadingWrapper>
            )}
            {!isActive &&
              isNearBy &&
              !isLoading &&
              !isDrmTesting &&
              !needStreamTokenForPresetSD &&
              this.renderPreloader()}
            {this.getShouldRenderPlayer() && this.renderPlayer()}
            <SwipeableViews
              index={activeUIIndex}
              onChangeIndex={this.handleSlideChange}
              disabled={shouldDisableSwiper || !isOnMobile}
              enableMouseEvents
              style={{ position: 'relative', zIndex: '-1' }}
            >
              <SwipableUI height={SwipableUIHeight}>
                <CleanModeContainer>
                  <LivestreamDetailOnlineFooterMobile
                    streamId={streamId}
                    isStreamActivated={isActive}
                    handleSlideChange={this.handleSlideChange}
                    isCleanMode
                  />
                </CleanModeContainer>
              </SwipableUI>
              <SwipableUI height={SwipableUIHeight}>
                <OnlineContainer ref={this.onlineContainerRef}>
                  {isActive && this.renderScrollHintUI()}
                  {isActive && (
                    <LiveStreamAnimationGift
                      streamId={streamId}
                      wrapperRef={this.onlineContainerRef}
                    />
                  )}
                  <LivestreamDetailOnlineHeader
                    streamId={streamId}
                    isActive={isActive}
                  />
                  {!needStreamTokenForExclusivePrivatePerforming && (
                    <>
                      {isActive && (
                        <LiveStreamFloatingAnimation streamId={streamId} />
                      )}
                      <Body>
                        <UserEnteredNoticeRow>
                          <UniversalComponent
                            filename="Container_LiveStreamUserEnteredNotice"
                            streamId={streamId}
                            importer={
                              /* #__LOADABLE__ */ () =>
                                import(
                                  '../container/LiveStreamUserEnteredNotice.js'
                                )
                            }
                          />
                        </UserEnteredNoticeRow>
                        <GiftRow>
                          <UniversalComponent
                            filename="Container_LiveStreamChatroomGift"
                            streamId={streamId}
                            importer={
                              /* #__LOADABLE__ */ () =>
                                import('../container/LiveStreamChatroomGift.js')
                            }
                          />
                        </GiftRow>
                        {isOnMobile ? (
                          <>
                            <ChatroomRow>
                              {isActive && (
                                <UniversalComponent
                                  filename="Container_LiveStreamChatroom"
                                  streamId={streamId}
                                  importer={
                                    /* #__LOADABLE__ */ () =>
                                      import(
                                        '../container/LiveStreamChatroom.js'
                                      )
                                  }
                                />
                              )}
                            </ChatroomRow>
                            <PinnedRow>{this.renderStreamerPinned()}</PinnedRow>
                          </>
                        ) : null}
                        {fundingId &&
                          (isOnMobile ? !isCleanMode : isAsideExpanded) &&
                          (isOverlayCta ? !isPerforming : true) && (
                            <PrivateShowSummaryRow>
                              <UniversalComponent
                                filename="Container_PrivateShowSummary"
                                streamId={streamId}
                                importer={
                                  /* #__LOADABLE__ */ () =>
                                    import('../container/PrivateShowSummary.js')
                                }
                              />
                            </PrivateShowSummaryRow>
                          )}
                        {isActive &&
                          isOnMobile &&
                          shouldShowLivestreamCta &&
                          !isOverlayCta && <CtaRow>{this.renderCta()}</CtaRow>}
                        <ControlButtonRow>
                          <LivestreamDetailOnlineFooterDesktop
                            streamId={streamId}
                            isStreamActivated={isActive}
                          />
                        </ControlButtonRow>
                      </Body>
                      {isActive && !this.getIsOverlayCta() && (
                        <StyledDesktopLiveStreamCountDown>
                          <ModalTracking id="LiveStreamCountDown">
                            <UniversalComponent
                              filename="Container_LiveStreamCountDown"
                              streamId={streamId}
                              importer={
                                /* #__LOADABLE__ */ () =>
                                  import('../container/LiveStreamCountDown.js')
                              }
                            />
                          </ModalTracking>
                        </StyledDesktopLiveStreamCountDown>
                      )}
                      <FooterMobileWrapper>
                        <LivestreamDetailOnlineFooterMobile
                          activeUIIndex={activeUIIndex}
                          streamId={streamId}
                          isStreamActivated={isActive}
                          isNormalMode
                        />
                      </FooterMobileWrapper>
                    </>
                  )}
                  {isActive &&
                    PRESET_SD === streamStatus &&
                    ((!hasStreamToken && !isExclusivePrivateOngoing) ||
                      shouldDisplayExclusivePrivateOverlayCta) && (
                      <LivestreamOverlayCtaWrapper>
                        <ModalTracking id="LivestreamOverlayCta">
                          <LivestreamOverlayCta streamId={streamId} />
                        </ModalTracking>
                      </LivestreamOverlayCtaWrapper>
                    )}
                  {isActive && (
                    <LivestreamUserTriggerModals streamId={streamId} />
                  )}
                </OnlineContainer>
              </SwipableUI>
            </SwipeableViews>
            {this.renderRetryPlayButton()}
            {this.renderClosePanel()}

            {isActive && (
              <UniversalComponent
                filename="Container_LiveStreamChatHandler"
                streamId={streamId}
                importer={
                  /* #__LOADABLE__ */ () =>
                    import('../container/LiveStreamChatHandler.js')
                }
              />
            )}
          </StreamSide>
        </PlayerContext.Provider>
        {isActive && !isOnMobile && (
          <AsideWrapper isAsideExpanded={isAsideExpanded}>
            {isAsideExpanded && (
              <UniversalComponent
                filename="Container_LivestreamAside"
                streamId={streamId}
                userId={userId}
                importer={
                  /* #__LOADABLE__ */ () =>
                    import('../container/LivestreamAside.js')
                }
              />
            )}
          </AsideWrapper>
        )}
      </Container>
    );
  };
  render() {
    const {
      isLiveStreaming,
      isActive,
      isNearBy,
      streamId,
      userId,
      deviceHeight,
      isOnMobile,
      isOtherUserExclusivePrivateBuyer,
      isExclusivePrivatePerforming,
      drmTestStatus,
    } = this.props;
    const isDrmTesting = drmRulesetTestStatusConstant.TESTING === drmTestStatus;
    const isLoading = this.getIsLoading();
    const isOffline = isLiveStreaming === false;

    const shouldDisplayOnlinePanels =
      isDrmTesting ||
      isLoading ||
      (isLiveStreaming && !isOtherUserExclusivePrivateBuyer);

    return (
      <>
        {(isActive || isNearBy) && (
          <StyledLiveStream deviceHeight={deviceHeight} isOnMobile={isOnMobile}>
            {shouldDisplayOnlinePanels
              ? this.renderOnlinePanels()
              : this.renderOfflinePanels({
                  isExclusivePrivateLayout: isExclusivePrivatePerforming,
                })}
          </StyledLiveStream>
        )}
        {isActive && !isOffline ? (
          <LivestreamPusherHandlerWrapper>
            <UniversalComponent
              filename="Container_LiveStreamPusherHandler"
              streamId={streamId}
              style={{ height: 0 }}
              importer={
                /* #__LOADABLE__ */ () =>
                  import('../container/LiveStreamPusherHandler.js')
              }
            />
          </LivestreamPusherHandlerWrapper>
        ) : null}
        {isActive && (
          <LivestreamDetailModals
            streamId={streamId}
            userId={userId}
            reloadPlayer={this.reloadPlayer}
            handleCleanModeChange={this.handleSlideChange}
          />
        )}
        {isActive && (
          <ModalView
            id="GiftPromotionModal"
            modalId={ModalId.LiveStream.ModalGiftSendPopup}
            render={() => (
              <UniversalComponent
                filename="Container_GiftPromotionModal"
                streamId={streamId}
                modalId={ModalId.LiveStream.ModalGiftSendPopup}
                importer={
                  /* #__LOADABLE__ */ () =>
                    import('../container/GiftPromotionModal.js')
                }
              />
            )}
          />
        )}
        {isActive && (
          <UniversalComponent
            filename="Container_GiftPromotionHandler"
            streamId={streamId}
            importer={
              /* #__LOADABLE__ */ () =>
                import('../container/GiftPromotionHandler.js')
            }
          />
        )}
        {isActive && <LivestreamRatingHintHandler streamId={streamId} />}
      </>
    );
  }
}

LiveStream.propTypes = {
  t: PropTypes.func.isRequired,
  privateShowStatus: PropTypes.string,
  meId: PropTypes.string,
  userId: PropTypes.string,
  streamId: PropTypes.string,
  sourceId: PropTypes.string,
  provider: PropTypes.string,
  preset: PropTypes.string,
  fundingId: PropTypes.string,
  drmTestStatus: PropTypes.string,
  streamStatus: PropTypes.string,
  closeModal: PropTypes.func,
  openModal: PropTypes.func,
  isGiftPanelOpen: PropTypes.bool,
  isChatPanelOpen: PropTypes.bool,
  isTopLeaderboardScrolling: PropTypes.bool,
  deviceHeight: PropTypes.number,
  isLiveStreaming: PropTypes.bool,
  isActive: PropTypes.bool,
  isPrivateShowUnlocked: PropTypes.bool,
  isPrivateStreamUnlocked: PropTypes.bool,
  hasStreamToken: PropTypes.bool,
  shouldShowLivestreamCta: PropTypes.bool,
  resetLiveStream: PropTypes.func,
  username: PropTypes.string,
  isNearBy: PropTypes.bool,
  keySystem: PropTypes.string,
  goBack: PropTypes.func,
  isPrivateStreamSubscribed: PropTypes.bool,
  isOnMobile: PropTypes.bool,
  isAsideExpanded: PropTypes.bool,
  hasScrollHintDisplayed: PropTypes.bool,
  shouldShowOperationHintUI: PropTypes.bool,
  shouldShowExitAlertModal: PropTypes.bool,
  needStreamTokenForExclusivePrivatePerforming: PropTypes.bool,
  isMeExclusivePrivateBuyer: PropTypes.bool,
  isExclusivePrivatePerforming: PropTypes.bool,
  isExclusivePrivatePreparing: PropTypes.bool,
  isOtherUserExclusivePrivateBuyer: PropTypes.bool,
  publicSourceFormat: PropTypes.string,
  privateSourceFormat: PropTypes.string,
  isLivestreamOverlayCtaEnabled: PropTypes.bool,
  isLivestreamModerator: PropTypes.bool,
  fetchStreamsToken: PropTypes.func,
  updateLiveStreamPreset: PropTypes.func,
  shouldShowRetryPlayButton: PropTypes.bool,
  setShouldShowRetryPlayButton: PropTypes.func,
  setPlayerData: PropTypes.func,
  cleanupAgoraToken: PropTypes.func,
  fetchRetainedEvents: PropTypes.func,
  setShouldDisplayCleanModeHint: PropTypes.func,
  shouldDisplayCleanModeHint: PropTypes.bool,
  setShouldDisplayAsideModuleHint: PropTypes.func,
  shouldDisplayAsideModuleHint: PropTypes.bool,
  videoHeight: PropTypes.number,
  videoWidth: PropTypes.number,
  setShouldDisplayHorizonLayoutHint: PropTypes.func,
  shouldDisplayHorizonLayoutHint: PropTypes.bool,
};

LiveStream.defaultProps = {
  privateShowStatus: null,
  meId: '',
  userId: '',
  streamId: null,
  preset: PRESET_PREVIEW,
  sourceId: '',
  provider: '',
  fundingId: null,
  drmTestStatus: null,
  streamStatus: null,
  closeModal: () => null,
  openModal: () => null,
  isGiftPanelOpen: false,
  isChatPanelOpen: false,
  isTopLeaderboardScrolling: false,
  deviceHeight: 0,
  isLiveStreaming: null,
  isActive: false,
  isPrivateShowUnlocked: false,
  isPrivateStreamUnlocked: false,
  hasStreamToken: false,
  shouldShowLivestreamCta: false,
  resetLiveStream: () => null,
  username: '',
  isNearBy: null,
  keySystem: null,
  goBack: () => null,
  isPrivateStreamSubscribed: false,
  isOnMobile: false,
  isAsideExpanded: true,
  hasScrollHintDisplayed: false,
  shouldShowOperationHintUI: false,
  shouldShowExitAlertModal: false,
  needStreamTokenForExclusivePrivatePerforming: false,
  publicSourceFormat: '',
  privateSourceFormat: '',
  isLivestreamOverlayCtaEnabled: false,
  isExclusivePrivatePerforming: false,
  isExclusivePrivatePreparing: false,
  isOtherUserExclusivePrivateBuyer: false,
  isMeExclusivePrivateBuyer: false,
  isLivestreamModerator: false,
  fetchStreamsToken: () => null,
  updateLiveStreamPreset: () => null,
  shouldShowRetryPlayButton: false,
  setShouldShowRetryPlayButton: () => null,
  setPlayerData: () => null,
  cleanupAgoraToken: () => null,
  fetchRetainedEvents: () => null,
  setShouldDisplayCleanModeHint: NULL_FUNCTION,
  shouldDisplayCleanModeHint: false,
  setShouldDisplayAsideModuleHint: NULL_FUNCTION,
  shouldDisplayAsideModuleHint: false,
  videoHeight: null,
  videoWidth: null,
  setShouldDisplayHorizonLayoutHint: NULL_FUNCTION,
  shouldDisplayHorizonLayoutHint: false,
};

const StyledLiveStream = styled.div`
  position: relative;
  width: 100vw;
  height: calc(100vh - ${headerHeight}px); /* fallback */
  height: calc(100dvh - ${headerHeight}px); /* modern browsers */
  margin-top: ${headerHeight}px;
  ${media.tablet`
    height: 100vh; /* fallback */
    height: 100dvh; /* modern browsers */
    margin-top: 0;
  `};
`;

const ClosePanel = styled.div`
  position: absolute;
  top: 10px;
  right: 16px;
  z-index: 2;
  padding: 6px;
  width: 40px;
  height: 40px;
  user-select: none;
  ${media.tablet`
    top: 10px;
    right: 8px;
  `};
`;

const SwipableUI = styled.div.attrs(({ height }) => ({
  style: {
    height: `${height}px`,
  },
}))`
  position: relative;
`;

const BackButton = styled.button`
  ${ResetButtonStyle}
  width: 100%;
  height: 100%;
  & > img {
    width: 100%;
    height: 100%;
    object-fit: contain;
  }
`;

const PlayerWrapper = styled.div`
  position: absolute;
  z-index: -1;
  top: 0px;
  left: 0px;
  right: 0px;
  bottom: 0px;
`;

const LivestreamOverlayCtaWrapper = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  z-index: 2;
  transform: translate(-50%, -50%);
`;

const LivestreamPusherHandlerWrapper = styled.div`
  display: inline-flex;
`;

const LiveStreamCover = styled.div`
  position: absolute;
  width: 100%;
  height: 100%;
  background-color: #191919;
  background-image: linear-gradient(
    to bottom,
    rgba(0, 0, 0, 0.5),
    rgba(0, 0, 0, 0.1) 20%,
    rgba(0, 0, 0, 0.04) 25%,
    rgba(0, 0, 0, 0.04) 33%,
    rgba(0, 0, 0, 0.5)
  );
`;
const LiveStreamCoverMask = styled.div`
  ${({ $showBackgroundGradient }) => {
    if ($showBackgroundGradient) {
      return css`
        background-image: linear-gradient(
          to bottom,
          rgba(0, 0, 0, 0.5),
          rgba(0, 0, 0, 0.1) 20%,
          rgba(0, 0, 0, 0.04) 25%,
          rgba(0, 0, 0, 0.04) 33%,
          rgba(0, 0, 0, 0.5)
        );
      `;
    } else {
      return css`
        background-color: rgba(0, 0, 0, 0.6);
      `;
    }
  }};
  position: absolute;
  top: 0;
  left: 0;
  bottom: 0;
  right: 0;
`;
const LiveStreamPlayerMask = styled.div`
  opacity: ${({ maskOpacity }) => maskOpacity};
  transition: opacity 0.3s ease-in-out;
  background-image: linear-gradient(
    to bottom,
    rgba(0, 0, 0),
    rgba(0, 0, 0, 0.1) 15%,
    rgba(0, 0, 0, 0.2) 50%,
    rgba(0, 0, 0, 0.8) 70%,
    rgba(0, 0, 0, 0.6)
  );
  position: absolute;
  top: 0;
  left: 0;
  bottom: 0;
  right: 0;
`;

const LoadingWrapper = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
`;

const LoadingWithTextWrapper = styled(LoadingWrapper)`
  max-width: 400px;
  text-align: center;
`;

const LoadingText = styled.div`
  margin-top: 12px;
  color: ${textColor.white};
  font-size: 14px;
  font-weight: 500;
  line-height: 1.17;
`;

const Image = styled.img`
  width: 100%;
  height: 100%;
  object-fit: contain;
`;
const Picture = styled.picture`
  > img {
    width: 100%;
    height: 100%;
    object-fit: contain;
  }
`;

const CleanModeContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  width: 100%;
  height: 100%;
`;

const OnlineContainer = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow-y: hidden;
`;

const Body = styled.div`
  position: relative;
  margin: auto 0 8px;
  display: flex;
  flex-direction: column;

  ${mediaHeight.mobileLandscape`
    @media (orientation: landscape) and (hover: none) {
      width: 50%;
      flex: 1;
      min-height: 0;
    }
  `};
`;

const ControlButtonRow = styled.div`
  z-index: 2; // set z-index to 2 to prevent the rating hint be covered by the chat message
  display: flex;
  justify-content: flex-end;
`;

const Row = styled.div`
  position: relative;
  z-index: 1;
  padding: 0px 12px 0px 8px;
  width: 100%;
  :last-child {
    margin-bottom: 0px;
  }
`;

const UserEnteredNoticeRow = styled(Row)`
  margin-bottom: 8px;
`;

const GiftRow = styled(Row)`
  margin-bottom: 20px;
  z-index: 0;
`;

const PrivateShowSummaryRow = styled(Row)`
  margin-bottom: 12px;
  padding: 0;

  ${mediaHeight.mobileLandscape`
    @media (orientation: landscape) and (hover: none) {
      width: 100vw;
    }
  `};
`;

const CtaRow = styled(Row)`
  margin-bottom: 12px;
`;

const PinnedRow = styled(Row)`
  margin-bottom: 12px;
`;

const ChatroomRow = styled(Row)`
  ${mediaHeight.mobileLandscape`
    @media (orientation: landscape) and (hover: none) {
      flex: 1;
      min-height: 0;
      overflow-y: auto;
    }
  `};
`;

const FooterMobileWrapper = styled.div`
  flex-shrink: 0;
`;

const Container = styled.div`
  display: flex;
  flex-direction: row;
  width: 100%;
  height: 100%;
`;

const StreamSide = styled.div`
  position: relative;
  max-width: 100%;
  flex: 1;
  z-index: 0;
`;

const AsideWrapper = styled.div`
  width: ${({ isAsideExpanded }) => (isAsideExpanded ? `${ASIDE_WIDTH}px` : 0)};
  background-color: ${color.grey[900]};
  transition: linear 0.2s;
  display: block;
  ${media.tablet`
    display: none;
  `}
`;

const ScrollHintUIWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
`;

const BlurredCover = styled.div`
  position: absolute;
  width: 100%;
  height: 100%;
  filter: blur(8px);
  img {
    object-fit: cover;
  }
  z-index: -1;
`;

const FadeIn = keyframes`
  from  {
    opacity: 0;
    transform: scale(2);
  }
  to  {
    opacity: 1;
    transform: scale(1);
  }
`;

const RetryPlayButton = styled.button`
  ${ResetButtonStyle};
  position: absolute;
  top: 50%;
  left: 50%;
  z-index: 1;
  transform: translate(-50%, -50%);
  > img {
    border-radius: 50%;
    padding: 6px;
    width: 72px;
    height: 72px;
    object-fit: contain;
    background-color: rgba(25, 25, 25, 0.6);
    animation: 400ms forwards ${FadeIn} ease-out;
  }
`;

const StyledDesktopLiveStreamCountDown = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  padding: 0px 8px
    max(16px, ${isIphoneXSeriesPWA ? iPhoneXSeriesFooterPaddingInPWA : 0}px);

  ${media.tablet`
    display: none;
  `}
`;

const LivestreamRatingHintHandler = ({ streamId }) => {
  useLivestreamRatingHint({ streamId });
  return null;
};

LivestreamRatingHintHandler.propTypes = {
  streamId: PropTypes.string.isRequired,
};

export default withTranslation()(LiveStream);
